import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, RequestError, Service } from 'webapp-types';
import type { BillingAddonType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type BillingPlan from 'webapp/models-custom/billing-plan';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';
import { Severity } from 'webapp/services/logger';

export interface Args {
	onClose: () => void;
	origin?: TrackingOriginTypeForTrialPopupOpened | null;
}

export default abstract class BillingQuantifiableAddonUpdateBaseComponent<
	T extends Args = Args,
> extends Component<T> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;

	@tracked confirmed = false;
	@tracked submitInProgress = false;

	// set once the purchase/change is confirmed; read at teardown to decide the
	// `choice` on the "...popup completed" tracking event
	private purchased = false;

	// quantity when the editor opened (what the user is currently paying for);
	// each subclass captures it as a field and seeds the tracked `quantity` from
	// it (must declare `initialQuantity` before `quantity` so the order is right)
	abstract initialQuantity: number;
	abstract quantity: number;

	// --- addon-specific config supplied by subclasses ---
	abstract addonType: BillingAddonType;
	abstract successMessage: string;

	// --- tracking config supplied by subclasses ---
	abstract openedEventName: TrackingEvent;
	abstract completedEventName: TrackingEvent;
	abstract buildOpenedProps(): APIData.Tracking.Data;
	abstract buildCompletedProps(): APIData.Tracking.Data;

	constructor(owner: Owner, args: T) {
		super(owner, args);

		// fire "...popup opened" after construction, so subclass field
		// initializers (initialQuantity, quantity, …) have run
		next(this, this.trackOpened);
	}

	// fires the "...popup completed" event on teardown (covers confirm, Cancel,
	// the X and overlay dismiss uniformly), reading live values
	override willDestroy(): void {
		super.willDestroy();
		this.analyticsService.event(this.completedEventName, {
			origin: this.args.origin,
			choice: this.purchased ? 'purchase' : 'cancel',
			...this.buildCompletedProps(),
		});
	}

	private trackOpened(): void {
		if (this.isDestroying || this.isDestroyed) {
			return;
		}
		this.analyticsService.event(this.openedEventName, {
			origin: this.args.origin,
			...this.buildOpenedProps(),
		});
	}

	get isDirty(): boolean {
		return this.quantity !== this.initialQuantity;
	}

	get isNotDirty(): boolean {
		return !this.isDirty;
	}

	// confirm button stays disabled until there is a change and the purchase is
	// confirmed; also blocked when we can't resolve a price to charge (adding only)
	get isConfirmDisabled(): boolean {
		return this.isNotDirty || !this.confirmed || (this.quantity > 0 && !this.hasValidPrice);
	}

	get activePlan(): BillingPlan | null {
		return this.billingService.activePlan;
	}

	// raw per-pack price (cents, monthly) on the active plan; undefined when the
	// addon price is missing from the plan data (do NOT coerce to 0 for billing)
	get addonPriceCents(): number | undefined {
		return this.activePlan?.getAddonPrice(this.addonType);
	}

	get hasValidPrice(): boolean {
		return this.addonPriceCents !== undefined;
	}

	// per pack, in cents, monthly - on our active plan (0 only for display fallback)
	get pricePerPackCents(): number {
		return this.addonPriceCents ?? 0;
	}

	@action
	onSliderChange(value: number): void {
		this.quantity = value;
	}

	@action
	onConfirmChange(value: boolean): void {
		this.confirmed = value;
	}

	@action
	save(): void {
		// SubmitButton delays onTrigger by 150ms, so a double-click schedules two
		// saves before submitInProgress disables the button - drop the second one
		if (this.submitInProgress) {
			return;
		}

		// never submit a purchase when the price shown was a fallback zero
		if (this.quantity > 0 && !this.hasValidPrice) {
			this.loggerService.logError(
				`[Billing] Missing ${this.addonType} addon price`,
				{ planId: this.activePlan?.id },
				Severity.CRITICAL,
			);
			this.infolayerService.error('Something went wrong. Please try again later.');
			return;
		}

		this.submitInProgress = true;

		const request =
			this.quantity > 0
				? this.billingService.addAddon(this.addonType, this.quantity)
				: this.billingService.removeAddon(this.addonType);

		request.then(
			() => {
				// popup closes on success (component is torn down), so there is
				// no need to reset quantity/confirmed here; flag the purchase so
				// the teardown-time "completed" event records choice: 'purchase'.
				// submitInProgress stays set, so a second save scheduled by a
				// double-click is dropped even when the request resolves before it
				this.purchased = true;
				this.infolayerService.ok(this.successMessage);
				this.args.onClose();
			},
			(errorData: RequestError) => {
				this.submitInProgress = false;
				this.requestService.handleGenericThrownError(errorData);
			},
		);
	}

	// locale-formats a count; when `addSign` is set, positive values get a
	// leading "+" (used for the "amount added" figures)
	protected formatCount(count: number, addSign = false): string {
		let formatted = count.toLocaleString('en-US');
		if (addSign && count > 0) {
			formatted = `+${formatted}`;
		}
		return formatted;
	}
}
